// Home (`/c/{slug}/home`; design spec §5; HOME-CAD-UX pass 2026-10-21): a personal start page,
// not a landing page. Linear's "My issues", Notion's home and Work OS's `command-home.tsx` are the
// references: one greeting line by the person's first name, then dense 36–44 px rows.
// HOME-DASH (Andrei, 2026-10-27: "not full width"; "people can reorganize things, like
// dashboards"): the page is a full-width widget grid (`DashboardGrid`) each person arranges —
// "Edit layout" in the header shows, hides, moves and resizes widgets, saved per person in
// `me/prefs/home_layout` (home-layout.ts); "Reset to default" saves `{}`. Edit mode is off on a
// phone (one column). A widget with nothing to show takes no cell. The widgets:
// - KPI score, Goal score, Cost (week): small tiles over the company roll-up
//   (`scorecards.company`; the API rolls up every seat with KPIs, people and DEs, so a
//   human-only company has numbers); no number yet is a small "Awaiting data" chip;
// - Agent runtime (week): runs, active time, tokens, cost (`workforce.runtime`), "—" with the
//   reason where the runs reported no usage; Live activity: the latest runs (`workforce.activity`);
// - Human hours (needs WorkPulse): the supervisor hours, hidden by default (Andrei: "I don't think
//   we should track human time yet unless we integrate WorkPulse");
// - Needs you: the caller's unread inbox (approvals, mentions, escalations…) and, for managers,
//   hours waiting for approval this week;
// - My work: open tasks on the seats the caller holds (`my.tasks`), Overdue · Today · Upcoming;
// - Scorecard: every roll-up seat with its holder, band and score (people and DEs alike);
// - Team scorecards (TEAM-KPI): the teams the caller may view that have KPIs, band and health;
// - Friday report: this week's version, one row;
// - the setup checklist (managers) until every step is done;
// - aside: Recent places (this browser) and Focus.
// A route the API does not serve answers null and its card is absent: nothing is a fake "0".
// MEMBER-UX: a plain member (not `company#reader`) gets the personal parts only — greeting, Needs
// you, My work, Recent — and, instead of the company scorecard, "My scorecard": the seats it
// holds that carry KPIs (`seats.scorecard`, `seat#can_view`). The tiles, the company scorecard
// and the Friday report are company reads the API refuses a member, so they are never asked.
import {
  AvatarTile,
  Badge,
  type Band,
  BandBadge,
  Button,
  buttonVariants,
  Checkbox,
  DashboardGrid,
  type DashboardItem,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DueLabel,
  EmptyState,
  formatMicroMoney,
  formatTokenCount,
  HomeCard,
  HomeCardDensity,
  HomeCardRow,
  InfoTip,
  Label,
  LensBandDot,
  PriorityMark,
  SetupSteps,
  toast,
  useIsMobile,
  workerName,
} from "@companyos/ui";
import { useInfiniteQuery, useQueries, useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ActivityIcon,
  AtSignIcon,
  BellIcon,
  BotIcon,
  CheckCircle2Icon,
  ClockIcon,
  FileTextIcon,
  GaugeIcon,
  HandIcon,
  HistoryIcon,
  InboxIcon,
  LayoutGridIcon,
  ListChecksIcon,
  MessageSquareReplyIcon,
  RotateCcwIcon,
  SparklesIcon,
  TriangleAlertIcon,
  WaypointsIcon,
} from "lucide-react";
import { type ReactNode, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { Scorecard, TimeEntry } from "../../api/types";
import { formatSubject, keepFocus, useFocus } from "../../focus/focus";
import { currentWeek, weekRangeLabel } from "../../focus/weeks";
import { useSubjectName } from "../../shell/focus-chips";
import type { ActivityRow } from "../agents/activity-screen";
import { useCostCurrency } from "../agents/agent-parts";
import { activityQuery } from "../agents/agent-queries";
import { AssessHomeCard } from "../assess/home-assess-card";
import { useWalletOn } from "../billing/use-wallet-on";
import { type CompanyAccess, isManager, isMember, isReader } from "../company";
import {
  formatDateTime,
  formatHours,
  formatMetricValue,
  formatMinutes,
  formatMoney,
  formatScore,
  formatTarget,
  REPORT_STATE_LABELS,
  TIME_KIND_LABELS,
} from "../format";
import { AskButton } from "../insights/ask-host";
import { PinnedAnswers } from "../insights/pinned-answers";
import { HEALTH_LABEL, HEALTH_TEXT, healthTone, lineHealth } from "../kpi/health";
import { teamScoresQuery } from "../kpi/team-queries";
import { healthCounts, teamBandTone } from "../kpi/team-scorecard";
import { NumbersDueCard } from "../numbers/my-numbers";
import {
  companyScorecardQuery,
  metricsQuery,
  peopleQuery,
  seatScorecardQuery,
  seatsQuery,
  weekReportsQuery,
  weekTimeQuery,
} from "../queries";
import { type QuickAddKind, useQuickAdd } from "../quick-add/quick-add-host";
import { LoadError, Loading, usePeriod } from "../screen-parts";
import { useFeature, useSession } from "../use-feature";
import { localDay, priorityKey } from "../work/model";
import {
  type LayoutEntry,
  moveWidget,
  resizeWidget,
  resolveLayout,
  setHidden,
  toPref,
  type WidgetId,
} from "./home-layout";
import {
  formatActive,
  greeting,
  greetingName,
  type Holder,
  holdersOf,
  inboxQuery,
  isForbidden,
  kpiTile,
  myMembershipId,
  myTasksQuery,
  orgChartQuery,
  peopleMeQuery,
  runtimeQuery,
  timeAgo,
} from "./home-queries";
import { type RecentPlace, readRecent } from "./recent-places";
import { useHomeLayout } from "./use-home-layout";

export function HomeScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const { me } = useSession();
  const kpi = useFeature("kpi");
  const week = usePeriod();
  const manager = isManager(company);
  // company#reader (manager | client viewer): the company's KPI roll-up and reports.
  const reader = isReader(company);
  const companyKpi = kpi.enabled && reader;
  const now = new Date();
  // The person's first name: `/v1/me`, else their person row in this company (one call, only
  // when the account has no name).
  const personRow = useQuery({ ...peopleMeQuery(api, company.id), enabled: !me.user.name });
  const fullName = me.user.name || personRow.data?.display_name || "";
  const firstName = greetingName(fullName, company.name, me.user.email);
  const day = now.toLocaleDateString("en-US", { weekday: "long", day: "numeric", month: "short" });
  const card = useQuery({ ...companyScorecardQuery(api, company.id, week), enabled: companyKpi });
  const chart = useQuery({ ...orgChartQuery(api, company.id), retry: false });
  const holders = holdersOf(chart.data);
  const mine = myMembershipId(me, company);
  const agents = useFeature("agents");

  // The person's layout (server-side, per person); edits show at once and save one at a time.
  // A pure client viewer keeps no prefs (the API refuses them): the default layout, no controls.
  // (`company#member`: owners, admins, agency operators and members; never a viewer grant.)
  const canEdit = isMember(company);
  // The checklist dismissed in this browser before HOME-DASH: honoured until the person shows
  // the widget again or resets the layout.
  const [legacyDismissed, setLegacyDismissed] = useState(() => readDismissed(company.id));
  const undismiss = () => {
    clearDismissed(company.id);
    setLegacyDismissed(false);
  };
  const layoutPref = useHomeLayout(api, company.id, me.user.id, canEdit);
  const layout = resolveLayout(layoutPref.value);
  const mobile = useIsMobile();
  const [editingRaw, setEditing] = useState(false);
  const editing = editingRaw && !mobile && canEdit;
  const [picking, setPicking] = useState(false);
  /** An edit of the newest layout (`null`: back to the default), saved behind. */
  const save = (edit: ((l: LayoutEntry[]) => LayoutEntry[]) | null, onSaved?: () => void) =>
    layoutPref.save((current) => (edit ? toPref(edit(resolveLayout(current))) : {}), {
      ...(onSaved ? { onSuccess: onSaved } : {}),
      onError: () => toast.add({ title: "The layout was not saved. Try again.", type: "error" }),
    });

  // What this person can have on Home (role and add-ons); the rest never shows or is offered.
  const available: Record<WidgetId, boolean> = {
    kpi_score: companyKpi,
    goal_score: companyKpi,
    cost_week: companyKpi,
    human_hours: companyKpi,
    agent_runtime: agents.enabled,
    live_activity: agents.enabled,
    setup: manager,
    pinned: manager,
    needs_you: true,
    my_work: true,
    numbers_due: kpi.enabled,
    assess: true,
    scorecard: reader,
    my_scorecard: !reader && kpi.enabled,
    team_scorecards: kpi.enabled,
    friday_report: companyKpi,
    recent: true,
    focus: true,
  };
  const offered = layout.filter((e) => available[e.id]);
  const shown = offered.filter((e) => !e.hidden);

  const content = (id: WidgetId): ReactNode => {
    switch (id) {
      case "kpi_score":
        return (
          <MiniTile
            slot="home-kpi-score"
            label="KPI score"
            info="The company's KPI roll-up this week: every seat with KPIs, people and DEs."
            pending={card.isPending}
            failed={card.isError}
            value={card.data?.kpi_score == null ? null : formatScore(card.data.kpi_score)}
            {...(card.data ? kpiTile(card.data) : {})}
          />
        );
      case "goal_score":
        return (
          <MiniTile
            slot="home-goal-score"
            label="Goal score"
            info="How the seats are doing against their goals this week."
            pending={card.isPending}
            failed={card.isError}
            value={card.data?.goal_score == null ? null : formatScore(card.data.goal_score)}
            detail={card.data ? `${card.data.children?.length ?? 0} seats scored` : undefined}
          />
        );
      case "cost_week":
        return (
          <MiniTile
            slot="home-cost-week"
            label="Cost (week)"
            info="What the DEs cost this week, from the cost ledger."
            pending={card.isPending}
            failed={card.isError}
            value={
              card.data?.cost
                ? formatMoney(card.data.cost.total_minor, card.data.cost.currency)
                : null
            }
            empty="No DE costs"
          />
        );
      case "human_hours":
        return (
          <MiniTile
            slot="home-human-hours"
            label="Human hours"
            info="Supervisor hours entered by hand. Human time is tracked once WorkPulse is connected."
            pending={card.isPending}
            failed={card.isError}
            value={card.data?.hours ? formatHours(card.data.hours.supervision_minutes) : null}
            empty="No hours"
          />
        );
      case "agent_runtime":
        return <AgentRuntimeCard api={api} company={company} week={week} />;
      case "live_activity":
        return <LiveActivityCard api={api} company={company} />;
      case "setup":
        return (
          <SetupChecklist
            api={api}
            company={company}
            week={week}
            seated={chart.isSuccess ? [...holders.values()].some((h) => h.length > 0) : undefined}
            numbersIn={card.data ? card.data.completeness > 0 : undefined}
            dismissed={legacyDismissed}
            onDismiss={() => save((l) => setHidden(l, "setup", true))}
          />
        );
      case "pinned":
        return <PinnedAnswers api={api} company={company} />;
      case "needs_you":
        return <NeedsYou api={api} company={company} week={week} manager={manager} />;
      case "my_work":
        return <MyWork api={api} company={company} />;
      case "numbers_due":
        // MEMBER-ENTRY: the numbers the caller reports for its own seats (absent without any).
        return <NumbersDueCard api={api} company={company} />;
      case "assess":
        // M8-ASSESS: a pending questionnaire; for managers, the latest round (absent without).
        return <AssessHomeCard api={api} company={company} manager={manager} />;
      case "scorecard":
        return (
          <SeatsCard
            company={company}
            kpi={kpi.enabled}
            card={card.data}
            pending={kpi.enabled ? card.isPending : chart.isPending}
            holders={holders}
            chartForbidden={chart.isError && isForbidden(chart.error)}
            seats={chart.data?.seats ?? []}
            mine={mine}
            manager={manager}
          />
        );
      case "my_scorecard":
        return (
          <MyScorecardCard
            api={api}
            company={company}
            week={week}
            seats={(chart.data?.seats ?? []).filter((s) =>
              (holders.get(s.id) ?? []).some((h) => h.membership_id === mine),
            )}
          />
        );
      case "team_scorecards":
        // TEAM-KPI: the teams the caller may view that have KPIs (absent without any).
        return <TeamScoresCard api={api} company={company} week={week} reader={reader} />;
      case "friday_report":
        return <ReportCard api={api} company={company} week={week} manager={manager} />;
      case "recent":
        return <RecentCard companyId={company.id} />;
      case "focus":
        return <FocusCard />;
    }
  };

  const items: DashboardItem[] = shown.map((e) => ({
    id: e.id,
    title: e.title,
    size: e.size,
    content: content(e.id),
  }));

  return (
    <div data-slot="home" className="flex w-full min-w-0 flex-col gap-4">
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
        <div
          data-slot="home-greeting"
          className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-3"
        >
          <h1 className="min-w-0 truncate font-heading text-[18px] leading-7 font-semibold tracking-heading text-foreground">
            {firstName ? `${greeting(now)}, ${firstName}` : greeting(now)}
          </h1>
          <span className="text-sm text-muted-foreground">
            {day} · W{week.slice(-2)} · {weekRangeLabel(week)}
            {week === currentWeek() ? "" : " · not this week"}
          </span>
        </div>
        {/* ADMIN-INSIGHTS: owners and admins only (renders nothing for members). */}
        <AskButton />
        {/* Moving and resizing is a desktop tool: on a phone Home is one column, and only the
            widget picker (show / hide) is offered. A client viewer keeps no prefs: no controls. */}
        {canEdit && layoutPref.failed ? (
          // The stored layout could not be read: no edits (they would overwrite it) until it is.
          <div data-slot="home-layout-actions" className="flex shrink-0 items-center gap-1.5">
            <span className="text-xs text-muted-foreground">Layout not loaded</span>
            <Button variant="ghost" size="sm" onClick={layoutPref.retry}>
              <RotateCcwIcon data-icon="inline-start" />
              Retry
            </Button>
          </div>
        ) : canEdit ? (
          <div data-slot="home-layout-actions" className="flex shrink-0 items-center gap-1.5">
            {editing || mobile ? (
              <Button
                variant="ghost"
                size="sm"
                disabled={!layoutPref.ready}
                onClick={() => setPicking(true)}
              >
                <LayoutGridIcon data-icon="inline-start" />
                Widgets
              </Button>
            ) : null}
            {editing ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    save(null, () => {
                      undismiss();
                      toast.add({ title: "Desk is back to the default layout.", type: "success" });
                    })
                  }
                >
                  <RotateCcwIcon data-icon="inline-start" />
                  Reset to default
                </Button>
                <Button size="sm" onClick={() => setEditing(false)}>
                  Done
                </Button>
              </>
            ) : mobile ? null : (
              <Button
                variant="ghost"
                size="sm"
                disabled={!layoutPref.ready}
                onClick={() => setEditing(true)}
              >
                <LayoutGridIcon data-icon="inline-start" />
                Edit layout
              </Button>
            )}
          </div>
        ) : null}
      </div>
      <HomeCardDensity.Provider value="compact">
        <DashboardGrid
          aria-label="Desk widgets"
          items={items}
          editing={editing}
          onMove={(id, index) =>
            save((l) =>
              moveWidget(
                l,
                l.filter((e) => available[e.id] && !e.hidden).map((e) => e.id),
                id,
                index,
              ),
            )
          }
          onResize={(id, size) => save((l) => resizeWidget(l, id as WidgetId, size))}
          onHide={(id) => save((l) => setHidden(l, id as WidgetId, true))}
        />
      </HomeCardDensity.Provider>
      {items.length === 0 && canEdit ? (
        <p className="text-sm text-muted-foreground">
          Every widget is hidden.{" "}
          <Button variant="link" size="sm" className="h-auto p-0" onClick={() => setPicking(true)}>
            Show widgets
          </Button>
        </p>
      ) : null}
      <WidgetPicker
        open={picking}
        onOpenChange={setPicking}
        // A checklist dismissed by the old browser flag is off here too: one tick shows it.
        widgets={offered.map((e) =>
          e.id === "setup" && legacyDismissed ? { ...e, hidden: true } : e,
        )}
        onToggle={(id, on) => {
          if (on && id === "setup") undismiss();
          save((l) => setHidden(l, id, !on));
        }}
      />
    </div>
  );
}

// Layout pieces -----------------------------------------------------------------------------------

/** Show or hide each widget this person can have (a modal: the page stays put). */
function WidgetPicker({
  open,
  onOpenChange,
  widgets,
  onToggle,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  widgets: readonly LayoutEntry[];
  onToggle: (id: WidgetId, on: boolean) => void;
}) {
  const id = useId();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Desk widgets</DialogTitle>
        </DialogHeader>
        <ul className="flex flex-col gap-0.5" data-slot="home-widget-picker">
          {widgets.map((w) => (
            <li key={w.id} className="flex min-h-8 items-center gap-2">
              <Checkbox
                id={`${id}-${w.id}`}
                checked={!w.hidden}
                onCheckedChange={(on) => onToggle(w.id, on === true)}
              />
              <Label htmlFor={`${id}-${w.id}`} className="font-normal">
                {w.title}
              </Label>
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A small number tile: label (with its (i)), the number, one muted line. No number yet is a
 * small "Awaiting data" chip, not a headline (Andrei, 2026-10-27: "simpler, don't need to make
 * it that big").
 */
function MiniTile({
  slot,
  label,
  info,
  value,
  pending,
  failed,
  band,
  detail,
  empty = "Awaiting data",
}: {
  slot: string;
  label: string;
  info: string;
  value: string | null;
  pending: boolean;
  failed: boolean;
  band?: Band | undefined;
  detail?: string | undefined;
  /** The chip when there is no number. */
  empty?: string;
}) {
  return (
    <div
      data-slot={slot}
      className="flex min-w-0 flex-col justify-center gap-0.5 rounded-lg border border-border bg-card px-3 py-2"
    >
      <div className="flex min-h-5 min-w-0 items-center gap-1 text-xs text-muted-foreground">
        <span className="truncate">{label}</span>
        <InfoTip label={`About ${label}`}>{info}</InfoTip>
        {value !== null && band ? (
          <span className="ml-auto">
            <BandBadge band={band} />
          </span>
        ) : null}
      </div>
      {pending ? (
        <span className="h-7 text-xl leading-7 text-muted-foreground">…</span>
      ) : failed ? (
        <span className="text-xs text-danger-text">Couldn't load</span>
      ) : value === null ? (
        <span
          data-slot="awaiting-data"
          className="w-fit rounded-sm border border-subtle bg-sunken px-1.5 py-0.5 text-xs text-muted-foreground"
        >
          {empty}
        </span>
      ) : (
        <span className="font-heading text-xl leading-7 font-semibold tracking-[-0.5px] tabular-nums">
          {value}
        </span>
      )}
      {detail && !pending && !failed ? (
        <span className="truncate text-xs text-muted-foreground">{detail}</span>
      ) : null}
    </div>
  );
}

// Agent runtime -----------------------------------------------------------------------------------

/**
 * The week's runs across the company's DEs and AGs (an AG runs as its DE): how many, how long,
 * tokens and cost. Usage a run never reported is "—" with the reason, never a zero.
 */
function AgentRuntimeCard({
  api,
  company,
  week,
}: {
  api: Api;
  company: CompanyAccess;
  week: string;
}) {
  const q = useQuery(runtimeQuery(api, company.id, week, company.calendar));
  const currency = useCostCurrency(api, company.id);
  const walletOn = useWalletOn(api, company.id);
  const s = q.data;
  const noUsage = s ? s.metered === 0 : false;
  const partly =
    s && s.unreported > 0 && s.metered > 0
      ? `${s.unreported} of ${s.runs} runs reported no usage; the total leaves them out.`
      : null;
  const unrecorded =
    "Not recorded yet: these runs reported no tokens or cost (self-hosted runs don't report usage yet).";
  return (
    <HomeCard
      title={`Agent runtime · W${week.slice(-2)}`}
      flush
      data-slot="home-agent-runtime"
      action={
        <Link
          to="/c/$slug/agents/activity"
          params={{ slug: company.slug }}
          search={keepFocus as never}
          className={buttonVariants({ variant: "ghost", size: "sm" })}
        >
          Activity
        </Link>
      }
    >
      {q.isPending ? (
        <div className="p-3">
          <Loading label="Loading agent runtime" rows={1} />
        </div>
      ) : q.isError ? (
        <div className="p-3">
          <LoadError what="Agent runtime" error={q.error} />
        </div>
      ) : !s || s.runs === 0 ? (
        <QuietLine>No agent runs this week.</QuietLine>
      ) : (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 px-3 py-2.5 sm:grid-cols-4">
          <RuntimeStat label="Runs" slot="runs">
            {s.runs.toLocaleString("en-US")}
            <span className="ml-1.5 text-xs font-normal text-muted-foreground">
              {s.des === 1 ? "1 DE" : `${s.des} DEs`}
              {s.live ? ` · ${s.live} live` : ""}
            </span>
          </RuntimeStat>
          <RuntimeStat label="Active time" slot="active">
            {formatActive(s.active_ms)}
          </RuntimeStat>
          {walletOn ? null : (
            <RuntimeStat
              label="Tokens"
              slot="tokens"
              info={noUsage ? unrecorded : (partly ?? undefined)}
              title={noUsage ? undefined : `${s.tokens_in} in · ${s.tokens_out} out`}
            >
              {noUsage ? "—" : formatTokenCount(s.tokens_in + s.tokens_out)}
            </RuntimeStat>
          )}
          <RuntimeStat label="Cost" slot="cost" info={noUsage ? unrecorded : (partly ?? undefined)}>
            {noUsage ? "—" : formatMicroMoney(s.cost_micros, currency)}
          </RuntimeStat>
        </dl>
      )}
    </HomeCard>
  );
}

function RuntimeStat({
  label,
  slot,
  info,
  title,
  children,
}: {
  label: string;
  slot: string;
  info?: string | undefined;
  title?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div data-runtime={slot} className="min-w-0">
      <dt className="flex min-h-5 items-center gap-1 text-xs text-muted-foreground">
        {label}
        {info ? <InfoTip label={`About ${label.toLowerCase()}`}>{info}</InfoTip> : null}
      </dt>
      <dd
        className="truncate font-heading text-lg leading-6 font-semibold tabular-nums"
        {...(title ? { title } : {})}
      >
        {children}
      </dd>
    </div>
  );
}

// Live activity -----------------------------------------------------------------------------------

const RUN_STATE: Record<string, { label: string; tone: string }> = {
  queued: { label: "Queued", tone: "bg-border-strong" },
  running: { label: "Running", tone: "bg-accent" },
  waiting_children: { label: "Running", tone: "bg-accent" },
  waiting_input: { label: "Waiting", tone: "bg-warning" },
  awaiting_approval: { label: "Waiting", tone: "bg-warning" },
  completed: { label: "Done", tone: "bg-success" },
  cancelled: { label: "Stopped", tone: "bg-border-strong" },
  failed: { label: "Failed", tone: "bg-danger-text" },
};

/** The latest runs of the digital workforce (Workforce › Activity's feed: live first). */
function LiveActivityCard({ api, company }: { api: Api; company: CompanyAccess }) {
  const feed = useInfiniteQuery(activityQuery(api, company.id));
  const first = feed.data?.pages[0];
  const rows: ActivityRow[] = first ? [...first.now, ...first.data].slice(0, 6) : [];
  return (
    <HomeCard
      title="Live activity"
      count={first ? first.now.length : undefined}
      flush
      data-slot="home-live-activity"
      action={
        <Link
          to="/c/$slug/agents/activity"
          params={{ slug: company.slug }}
          search={keepFocus as never}
          className={buttonVariants({ variant: "ghost", size: "sm" })}
        >
          All activity
        </Link>
      }
    >
      {feed.isPending ? (
        <div className="p-3">
          <Loading label="Loading activity" rows={2} />
        </div>
      ) : feed.isError ? (
        <div className="p-3">
          <LoadError what="Activity" error={feed.error} />
        </div>
      ) : rows.length === 0 ? (
        <QuietLine>No agent runs yet.</QuietLine>
      ) : (
        rows.map((r) => {
          const state = RUN_STATE[r.run.state] ?? { label: r.run.state, tone: "bg-border-strong" };
          const what = r.last?.text ?? r.task?.title ?? r.run.summary ?? null;
          return (
            <HomeCardRow
              key={r.run.id}
              data-run={r.run.id}
              lead={<ActivityIcon aria-hidden="true" className="size-4 text-faint" />}
              title={
                <Link
                  to={`/c/${company.slug}/agents/runs/${r.run.id}` as never}
                  className="truncate hover:underline"
                >
                  <span className="font-medium">{workerName(r.who.name)}</span>
                  {what ? <span className="text-muted-foreground"> · {what}</span> : null}
                </Link>
              }
              trailing={
                <>
                  <span className="inline-flex items-center gap-1.5">
                    <span aria-hidden="true" className={`size-1.5 rounded-full ${state.tone}`} />
                    {state.label}
                  </span>
                  <span>{timeAgo(r.run.started_at ?? r.run.created_at)}</span>
                </>
              }
            />
          );
        })
      )}
    </HomeCard>
  );
}

// Needs you ---------------------------------------------------------------------------------------

const KIND_ICON: Record<string, typeof BellIcon> = {
  mention: AtSignIcon,
  reply: MessageSquareReplyIcon,
  approval: HandIcon,
  agent_escalation: TriangleAlertIcon,
  assignment: ListChecksIcon,
  reminder: BellIcon,
  budget_alert: TriangleAlertIcon,
  kpi_correction: GaugeIcon,
  memory: SparklesIcon,
  task_change: ListChecksIcon,
  system: BellIcon,
  agent_directed: WaypointsIcon,
};

const KIND_LABEL: Record<string, string> = {
  mention: "Mention",
  reply: "Reply",
  approval: "Approval",
  agent_escalation: "Escalation",
  assignment: "Assigned to you",
  reminder: "Reminder",
  budget_alert: "Budget",
  kpi_correction: "Number corrected",
  memory: "Memory",
  task_change: "Task changed",
  system: "Notice",
  agent_directed: "Agent directed",
};

/** Where an inbox item opens (the same places the inbox drawer goes). */
export function inboxHref(
  slug: string,
  item: { subject_kind: string; subject_id: string },
): string {
  const base = `/c/${slug}`;
  switch (item.subject_kind) {
    case "task":
      return `${base}/work?task=${item.subject_id}`;
    case "page":
      // UPLOAD-PRIVATE-FIRST: an uploaded doc's text was read (or not): the doc.
      return `${base}/work/p/${item.subject_id}`;
    case "message":
    case "topic":
      return `${base}/chat`;
    case "approval":
      return `${base}/agents/approvals/${item.subject_id}`;
    case "run":
      return `${base}/agents/runs/${item.subject_id}`;
    case "delegation_request":
      return `${base}/agents/delegations/${item.subject_id}`;
    case "budget":
      return `${base}/setup/budgets`;
    case "observation":
      // MEMBER-ENTRY: a corrected number — the scorecard grid where it shows.
      return `${base}/kpis/scorecard`;
    case "reporting_duty":
      return `${base}/setup/kpi-reporting`;
    case "agent_request":
      // MANAGED-POLICY: a request for an agent (or its outcome): the DE list's Requests.
      return `${base}/agents`;
    case "de_brief":
      // COS-BRIEF-RULES: a Chief of Staff report delivered to the inbox: its full page.
      return `${base}/briefs/${item.subject_id}`;
    case "wallet":
    case "company_subscription":
    // SKILLSMITH-OPPORTUNITIES: a helper quote is approved or declined on Billing.
    case "helper_offer":
      return `${base}/setup/billing`;
    case "model_access":
      // MODEL-KEYS-PLANS: the plan stopped allowing own keys: Connections › API keys.
      return `${base}/agents/connections?tab=keys`;
    case "billing_company":
      return `/operator/billing?tab=subscriptions&company=${item.subject_id}`;
    case "action_outbox":
      // STEP1-OUTBOX: a dead letter waits in the Outbox's failed items.
      return `${base}/agents/outbox?tab=failed&item=${item.subject_id}`;
    case "integration_job":
      // INC2-INTAKE: an integration dead letter waits in Intake's dead letters.
      return `${base}/agents/intake?tab=dead`;
    case "portfolio_company":
      // FOUNDER-OS phase 1: a portfolio alert opens the held company's page in Founder OS.
      return `${base}/portfolio/companies/${item.subject_id}`;
    case "chief_of_staff":
      // COS-MODES: the CEO's "switch to Team?" suggestion opens Setup › Chief of Staff.
      return `${base}/setup/chief-of-staff`;
    default:
      return `${base}/desk`;
  }
}

function NeedsYou({
  api,
  company,
  week,
  manager,
}: {
  api: Api;
  company: CompanyAccess;
  week: string;
  manager: boolean;
}) {
  // INBOX-PAGE-MAIN: the Inbox is a page (the drawer is retired).
  const inboxLink = <Link to="/c/$slug/inbox" params={{ slug: company.slug }} />;
  const inbox = useQuery(inboxQuery(api, company.id, "unread"));
  // Hours waiting for a decision this week (managers approve them on the hours screen).
  const time = useInfiniteQuery({ ...weekTimeQuery(api, company.id, week), enabled: manager });
  const pendingHours = (time.data?.pages.flatMap((p) => p.data) ?? []).filter(
    (r) => r.approval_status === "pending",
  );
  // The inbox route is not served by this API: the card is absent, not empty.
  if (inbox.isSuccess && inbox.data === null && !pendingHours.length) return null;
  const items = inbox.data?.data ?? [];
  const count = items.length + pendingHours.length;
  const shown = items.slice(0, 6);
  return (
    <HomeCard
      title="Needs you"
      count={inbox.isSuccess || time.isSuccess ? count : undefined}
      flush
      data-slot="home-needs-you"
      action={
        inbox.data ? (
          <Button variant="ghost" size="sm" render={inboxLink}>
            <InboxIcon data-icon="inline-start" />
            Inbox
          </Button>
        ) : null
      }
    >
      {inbox.isPending ? (
        <div className="p-3">
          <Loading label="Loading what needs you" rows={2} />
        </div>
      ) : inbox.isError ? (
        <div className="p-3">
          <LoadError what="Your inbox" error={inbox.error} />
        </div>
      ) : count === 0 ? (
        <QuietLine>Nothing is waiting on you.</QuietLine>
      ) : (
        <>
          {pendingHours.slice(0, 3).map((r) => (
            <PendingHoursRow key={r.id} entry={r} slug={company.slug} />
          ))}
          {shown.map((item) => {
            const Icon = KIND_ICON[item.kind] ?? BellIcon;
            return (
              <HomeCardRow
                key={item.id}
                data-inbox-item={item.id}
                className="min-h-9 py-1"
                lead={<Icon aria-hidden="true" className="size-4 text-faint" />}
                title={
                  <Link
                    to={inboxHref(company.slug, item) as never}
                    className="truncate hover:underline"
                  >
                    {item.summary}
                  </Link>
                }
                trailing={
                  <>
                    <span className="text-xs text-muted-foreground">{KIND_LABEL[item.kind]}</span>
                    <span className="text-xs text-muted-foreground">
                      {timeAgo(item.created_at)}
                    </span>
                  </>
                }
              />
            );
          })}
          {items.length > shown.length ? (
            <div className="flex h-9 items-center border-t border-subtle px-4 text-xs text-muted-foreground">
              <Button variant="ghost" size="sm" render={inboxLink}>
                See all {items.length} in the inbox
              </Button>
            </div>
          ) : null}
        </>
      )}
    </HomeCard>
  );
}

function PendingHoursRow({ entry, slug }: { entry: TimeEntry; slug: string }) {
  return (
    <HomeCardRow
      data-pending-hours={entry.id}
      className="min-h-9 py-1"
      lead={<ClockIcon aria-hidden="true" className="size-4 text-faint" />}
      title={
        <Link
          to="/c/$slug/kpis/entry/hours"
          params={{ slug }}
          search={keepFocus}
          className="truncate hover:underline"
        >
          Approve {formatMinutes(entry.minutes)} of{" "}
          {(TIME_KIND_LABELS[entry.kind] ?? entry.kind).toLowerCase()}
        </Link>
      }
      trailing={<span className="text-xs text-muted-foreground">{entry.work_date}</span>}
    />
  );
}

/** Work OS `QuietLine`: one 44 px line with a green check when a list has nothing to show. */
function QuietLine({ children }: { children: ReactNode }) {
  return (
    <p className="flex min-h-11 items-center gap-2 px-4 text-sm text-muted-foreground">
      <CheckCircle2Icon aria-hidden="true" className="size-4 text-success" />
      {children}
    </p>
  );
}

// My work -----------------------------------------------------------------------------------------

const GROUPS = [
  ["overdue", "Overdue"],
  ["today", "Today"],
  ["upcoming", "Upcoming"],
  ["none", "No date"],
] as const;

function MyWork({ api, company }: { api: Api; company: CompanyAccess }) {
  const tasks = useQuery(myTasksQuery(api, company.id));
  if (tasks.isError) return null; // Work is not served by this API (or is being rebuilt).
  const rows = tasks.data?.data ?? [];
  const today = localDay(new Date());
  const bucket = (due: string | null) => {
    if (!due) return "none";
    const d = localDay(due);
    return d < today ? "overdue" : d === today ? "today" : "upcoming";
  };
  const sorted = [...rows].sort(
    (a, b) => (a.due_at ?? "9999").localeCompare(b.due_at ?? "9999") || a.priority - b.priority,
  );
  const shown = sorted.slice(0, 8);
  return (
    <HomeCard
      title="My work"
      count={tasks.isSuccess ? rows.length : undefined}
      flush
      data-slot="home-my-work"
      action={
        <Link
          to={"/c/$slug/work/my" as never}
          params={{ slug: company.slug } as never}
          className={buttonVariants({ variant: "ghost", size: "sm" })}
        >
          All my tasks
        </Link>
      }
    >
      {tasks.isPending ? (
        <div className="p-3">
          <Loading label="Loading your tasks" rows={2} />
        </div>
      ) : rows.length === 0 ? (
        <QuietLine>No open task on your seats.</QuietLine>
      ) : (
        GROUPS.map(([key, label]) => {
          const group = shown.filter((t) => bucket(t.due_at) === key);
          if (!group.length) return null;
          return (
            <div key={key} data-group={key}>
              <div className="flex h-8 items-center gap-1.5 bg-sunken px-4 text-xs font-medium text-secondary">
                {label}
                <span className="text-muted-foreground tabular-nums">{group.length}</span>
              </div>
              {group.map((t) => {
                const priority = priorityKey(t.priority);
                return (
                  <HomeCardRow
                    key={t.id}
                    data-task={t.id}
                    className="min-h-9 py-1"
                    lead={
                      <span
                        aria-hidden="true"
                        className={
                          t.blocked
                            ? "size-2.5 rounded-full border border-danger-text"
                            : "size-2.5 rounded-full border border-border-strong"
                        }
                      />
                    }
                    title={
                      <Link
                        to={"/c/$slug/work" as never}
                        params={{ slug: company.slug } as never}
                        search={{ task: t.id } as never}
                        className="truncate hover:underline"
                      >
                        {t.title}
                      </Link>
                    }
                    trailing={
                      <>
                        {t.blocked ? <Badge variant="danger">Blocked</Badge> : null}
                        {t.due_at ? (
                          <DueLabel due={localDay(t.due_at)} overdue={key === "overdue"} />
                        ) : null}
                        {priority ? <PriorityMark priority={priority} /> : null}
                      </>
                    }
                  />
                );
              })}
            </div>
          );
        })
      )}
    </HomeCard>
  );
}

// Scorecard seats ---------------------------------------------------------------------------------

function SeatsCard({
  company,
  kpi,
  card,
  pending,
  holders,
  chartForbidden,
  seats,
  mine,
  manager,
}: {
  company: CompanyAccess;
  kpi: boolean;
  card: Scorecard | undefined;
  pending: boolean;
  holders: Map<string, Holder[]>;
  chartForbidden: boolean;
  seats: { id: string; title: string; status: string }[];
  mine: string | null;
  manager: boolean;
}) {
  const params = { slug: company.slug };
  // With kpi: the roll-up's children (the seats that are scored); without: every held seat.
  const rows: { id: string; title: string; child?: NonNullable<Scorecard["children"]>[number] }[] =
    kpi && card
      ? (card.children ?? []).map((c) => ({ id: c.subject.id, title: c.subject.name, child: c }))
      : seats.filter((s) => (holders.get(s.id) ?? []).length > 0);
  // The seats the person holds come first.
  const sorted = [...rows].sort((a, b) => {
    const am = (holders.get(a.id) ?? []).some((h) => h.membership_id === mine) ? 0 : 1;
    const bm = (holders.get(b.id) ?? []).some((h) => h.membership_id === mine) ? 0 : 1;
    return am - bm || a.title.localeCompare(b.title);
  });
  return (
    <HomeCard
      title="Scorecard"
      count={pending ? undefined : sorted.length}
      flush
      data-slot="home-seats"
      action={
        kpi ? (
          <Link
            to="/c/$slug/kpis/scorecard"
            params={params}
            search={keepFocus}
            className={buttonVariants({ variant: "ghost", size: "sm" })}
          >
            Open scorecard
          </Link>
        ) : null
      }
    >
      {pending ? (
        <div className="p-3">
          <Loading label="Loading the seats" rows={2} />
        </div>
      ) : sorted.length === 0 ? (
        <EmptyState
          variant="compact"
          icon={BotIcon}
          title={kpi ? "No seat has KPIs yet" : "No seat has a holder yet"}
          description={
            manager
              ? kpi
                ? "Put metrics on a seat under Setup › Seats; the person or DE holding it is scored from then on."
                : "Assign a person or a DE to a seat under Setup › Seats."
              : undefined
          }
          actions={
            manager ? (
              <Link
                to="/c/$slug/setup/seats"
                params={params}
                search={keepFocus}
                className={buttonVariants({ variant: "secondary", size: "sm" })}
              >
                Open seats
              </Link>
            ) : null
          }
        />
      ) : (
        sorted.slice(0, 10).map((row) => {
          const held = holders.get(row.id) ?? [];
          const lead = held[0];
          const de = held.find((h) => h.kind === "digital_employee");
          const isMine = held.some((h) => h.membership_id === mine);
          const name = lead?.name ?? (chartForbidden ? row.title : "Open seat");
          const link =
            kpi && de ? (
              <Link
                to="/c/$slug/agents/de/$id"
                params={{ slug: company.slug, id: de.membership_id }}
                search={(prev: Record<string, unknown>) => ({
                  ...keepFocus(prev),
                  tab: "scorecard" as const,
                })}
                className="truncate font-medium hover:underline"
              >
                {name}
              </Link>
            ) : kpi ? (
              <Link
                to="/c/$slug/kpis/scorecard"
                params={params}
                search={(prev) => ({
                  ...keepFocus(prev as Record<string, unknown>),
                  s: `seat:${row.id}`,
                })}
                className="truncate font-medium hover:underline"
              >
                {name}
              </Link>
            ) : (
              <span className="font-medium">{name}</span>
            );
          return (
            <HomeCardRow
              key={row.id}
              data-seat={row.id}
              lead={
                <AvatarTile
                  name={lead?.name ?? row.title}
                  kind={lead?.kind === "digital_employee" ? "agent" : "person"}
                  size="sm"
                />
              }
              title={
                <span className="flex min-w-0 items-center gap-2">
                  {link}
                  {isMine ? <Badge variant="accent">You</Badge> : null}
                </span>
              }
              description={lead && name !== row.title ? row.title : undefined}
              trailing={
                row.child ? (
                  <>
                    <BandBadge band={row.child.band} />
                    <span className="w-14 text-right text-sm text-foreground tabular-nums">
                      {row.child.kpi_score === null ? "—" : formatScore(row.child.kpi_score)}
                    </span>
                  </>
                ) : null
              }
            />
          );
        })
      )}
    </HomeCard>
  );
}

// My scorecard (a plain member) -------------------------------------------------------------------

/**
 * The member's own seats with KPIs: one `seats.scorecard` per held seat (`seat#can_view` lets a
 * holder read it). No seat, or no seat with a metric: no card. Each seat opens KPIs › My
 * scorecard, the member's view of its scorecard (the company scorecard is company#reader's).
 */
function MyScorecardCard({
  api,
  company,
  week,
  seats,
}: {
  api: Api;
  company: CompanyAccess;
  week: string;
  seats: { id: string; title: string }[];
}) {
  const cards = useQueries({
    queries: seats.map((s) => ({
      ...seatScorecardQuery(api, company.id, s.id, week),
      retry: false,
    })),
  });
  const scored = seats.flatMap((s, i) => {
    const data = cards[i]?.data;
    return data && data.metrics.length > 0 ? [{ seat: s, card: data }] : [];
  });
  if (scored.length === 0) return null;
  return (
    <HomeCard
      title="My scorecard"
      count={scored.reduce((n, s) => n + s.card.metrics.length, 0)}
      flush
      data-slot="home-my-scorecard"
    >
      {scored.map(({ seat, card }) => {
        const currency = card.cost?.currency ?? "USD";
        return (
          <div key={seat.id} data-seat={seat.id}>
            <HomeCardRow
              lead={<GaugeIcon aria-hidden="true" className="size-4 text-faint" />}
              title={
                <Link
                  to="/c/$slug/kpis/my-numbers"
                  params={{ slug: company.slug }}
                  search={(prev) => ({
                    ...keepFocus(prev as Record<string, unknown>),
                    tab: "scorecard" as const,
                  })}
                  className="truncate font-medium hover:underline"
                >
                  {seat.title}
                </Link>
              }
              trailing={
                <>
                  <BandBadge band={card.band} />
                  <span className="w-14 text-right text-sm text-foreground tabular-nums">
                    {card.kpi_score === null ? "—" : formatScore(card.kpi_score)}
                  </span>
                </>
              }
            />
            {card.metrics.slice(0, 6).map((m) => (
              <HomeCardRow
                key={m.metric_def_id}
                data-metric={m.key}
                className="min-h-9 py-1 pl-10"
                title={<span className="truncate text-secondary">{m.name}</span>}
                trailing={
                  <>
                    <span
                      className={`inline-flex items-center gap-1.5 text-sm tabular-nums ${HEALTH_TEXT[lineHealth(m) ?? "none"]}`}
                      title={HEALTH_LABEL[lineHealth(m) ?? "none"]}
                    >
                      <LensBandDot band={healthTone(lineHealth(m))} />
                      {m.actual === null ? "—" : formatMetricValue(m.actual, m.unit, currency)}
                    </span>
                    <span className="w-24 text-right text-xs text-muted-foreground tabular-nums">
                      {formatTarget(m, currency)}
                    </span>
                  </>
                }
              />
            ))}
          </div>
        );
      })}
    </HomeCard>
  );
}

// Team scorecards (TEAM-KPI, founder-approved 2026-10-21) ----------------------------------------

/**
 * Every team the caller may view that has KPIs, with its band, score and health counts for the
 * week (`scorecards.teams`); a reader's rows open KPIs › Scorecard, where the team KPIs are.
 */
function TeamScoresCard({
  api,
  company,
  week,
  reader,
}: {
  api: Api;
  company: CompanyAccess;
  week: string;
  reader: boolean;
}) {
  const scores = useQuery(teamScoresQuery(api, company.id, week));
  const rows = scores.data ?? [];
  if (rows.length === 0) return null;
  return (
    <HomeCard title="Team scorecards" count={rows.length} flush data-slot="home-team-scorecards">
      {rows.map((t) => (
        <HomeCardRow
          key={t.team_id}
          data-team={t.team_id}
          lead={<LensBandDot band={teamBandTone(t)} />}
          title={
            reader ? (
              <Link
                to="/c/$slug/kpis/scorecard"
                params={{ slug: company.slug }}
                search={keepFocus}
                className="truncate font-medium hover:underline"
              >
                {t.name}
              </Link>
            ) : (
              <span className="truncate font-medium">{t.name}</span>
            )
          }
          description={healthCounts(t)}
          trailing={
            <>
              <BandBadge band={t.band} />
              <span className="w-14 text-right text-sm text-foreground tabular-nums">
                {t.kpi_score === null ? "—" : formatScore(t.kpi_score)}
              </span>
            </>
          }
        />
      ))}
    </HomeCard>
  );
}

// Friday report -----------------------------------------------------------------------------------

function ReportCard({
  api,
  company,
  week,
  manager,
}: {
  api: Api;
  company: CompanyAccess;
  week: string;
  manager: boolean;
}) {
  const reports = useQuery(weekReportsQuery(api, company.id, week));
  const latest = [...(reports.data?.data ?? [])].sort((a, b) => b.version - a.version)[0];
  const label = `W${week.slice(-2)}`;
  // Viewers see sent reports only (server rule); with none, the card is absent.
  if (!manager && reports.isSuccess && !latest) return null;
  return (
    <HomeCard
      title={`Friday report · ${label}`}
      data-slot="home-report"
      action={
        <Link
          to="/c/$slug/kpis/reports/$week"
          params={{ slug: company.slug, week }}
          search={keepFocus}
          className={buttonVariants({ variant: "ghost", size: "sm" })}
        >
          {latest ? "Open" : `Open ${label}`}
        </Link>
      }
      flush
    >
      {reports.isPending ? (
        <div className="p-3">
          <Loading label="Loading the Friday report" rows={1} />
        </div>
      ) : reports.isError ? (
        <div className="p-3">
          <LoadError what="The Friday report" error={reports.error} />
        </div>
      ) : !latest ? (
        <HomeCardRow
          lead={<FileTextIcon aria-hidden="true" className="size-4 text-faint" />}
          title={`No report for ${label} yet`}
          description="Generated on Friday; a manager can generate it earlier from the report page."
        />
      ) : (
        <HomeCardRow
          lead={<FileTextIcon aria-hidden="true" className="size-4 text-faint" />}
          title={`Version ${latest.version}`}
          description={
            latest.sent_at
              ? `Sent ${formatDateTime(latest.sent_at)} to ${latest.recipients.length} ${latest.recipients.length === 1 ? "recipient" : "recipients"}`
              : latest.generated_at
                ? `Generated ${formatDateTime(latest.generated_at)}`
                : "Draft"
          }
          trailing={
            <>
              <Badge
                variant={
                  latest.state === "sent"
                    ? "success"
                    : latest.state === "failed"
                      ? "danger"
                      : "neutral"
                }
              >
                {REPORT_STATE_LABELS[latest.state]}
              </Badge>
              {manager && latest.report_stale && latest.state !== "sent" ? (
                <Badge variant="warning">Stale</Badge>
              ) : null}
            </>
          }
        />
      )}
    </HomeCard>
  );
}

// Setup checklist ---------------------------------------------------------------------------------

const DISMISS_KEY = (companyId: string) => `companyos.home.checklist.${companyId}`;

/** Dismissed in this browser before HOME-DASH (the layout keeps it since): still honoured. */
function readDismissed(companyId: string): boolean {
  try {
    return window.localStorage.getItem(DISMISS_KEY(companyId)) === "1";
  } catch {
    return false;
  }
}

/** Showing the checklist again (widget picker, Reset to default) clears the old browser flag. */
function clearDismissed(companyId: string): void {
  try {
    window.localStorage.removeItem(DISMISS_KEY(companyId));
  } catch {
    // Storage blocked: nothing was stored.
  }
}

function SetupChecklist({
  api,
  company,
  week,
  seated,
  numbersIn,
  dismissed,
  onDismiss,
}: {
  /** Dismissed in this browser before HOME-DASH (the old flag). */
  dismissed: boolean;
  /** Hides the widget in the person's layout. */
  onDismiss: () => void;
  api: Api;
  company: CompanyAccess;
  week: string;
  /** A seat has a holder (from the org chart; undefined while it loads). */
  seated: boolean | undefined;
  /** The roll-up has some data this week (undefined while it loads, or with kpi off). */
  numbersIn: boolean | undefined;
}) {
  const kpi = useFeature("kpi");
  const navigate = useNavigate();
  const quickAdd = useQuickAdd();
  const people = useInfiniteQuery({ ...peopleQuery(api, company.id), enabled: !dismissed });
  const seats = useInfiniteQuery({ ...seatsQuery(api, company.id), enabled: !dismissed });
  const metrics = useInfiniteQuery({
    ...metricsQuery(api, company.id),
    enabled: !dismissed && kpi.enabled,
  });
  const time = useInfiniteQuery({
    ...weekTimeQuery(api, company.id, week),
    enabled: !dismissed && !kpi.enabled,
  });
  if (dismissed) return null;
  const queries = [people, seats, ...(kpi.enabled ? [metrics] : [time])];
  if (queries.some((q) => q.isPending) || seated === undefined)
    return <Loading label="Loading the setup checklist" rows={1} />;
  if (queries.some((q) => q.isError)) return null; // each list's own page shows its error
  const count = (q: typeof people | typeof seats | typeof metrics | typeof time) =>
    (q.data?.pages as { data: unknown[] }[] | undefined)?.reduce((n, p) => n + p.data.length, 0) ??
    0;
  // Each step opens the screen that does it (design spec §5).
  const go = (
    to:
      | "/c/$slug/setup/people"
      | "/c/$slug/setup/seats"
      | "/c/$slug/setup/metrics"
      | "/c/$slug/kpis/scorecard"
      | "/c/$slug/kpis/entry/hours",
  ) => void navigate({ to, params: { slug: company.slug }, search: keepFocus as never });
  // MODALS: inviting, a new seat and a new metric open their dialog here, over Home.
  const steps: {
    done: boolean;
    label: string;
    hint: string;
    to: Parameters<typeof go>[0];
    quick?: QuickAddKind;
  }[] = [
    {
      done: count(people) >= 2,
      label: "Invite people",
      hint: "Owners, admins and client viewers",
      to: "/c/$slug/setup/people" as const,
      quick: "invite",
    },
    {
      done: count(seats) >= 1,
      label: "Create a seat",
      hint: "The role a person or a DE fills",
      to: "/c/$slug/setup/seats" as const,
      quick: "seat",
    },
    ...(kpi.enabled
      ? [
          {
            done: count(metrics) >= 1,
            label: "Define metrics",
            hint: "What the scorecard measures",
            to: "/c/$slug/setup/metrics" as const,
            // In the add-on's grace period metrics are read-only: the page, not a dialog.
            ...(kpi.readOnly ? {} : { quick: "metric" as const }),
          },
        ]
      : []),
    {
      done: seated,
      label: "Put someone on a seat",
      hint: "A person or a DE; their work starts to count",
      to: "/c/$slug/setup/seats" as const,
    },
    kpi.enabled
      ? {
          done: numbersIn === true,
          label: `Enter numbers for W${week.slice(-2)}`,
          hint: "Click a cell of the scorecard",
          to: "/c/$slug/kpis/scorecard" as const,
        }
      : {
          done: count(time) >= 1,
          label: `Enter hours for W${week.slice(-2)}`,
          hint: "Supervision and founder time",
          to: "/c/$slug/kpis/entry/hours" as const,
        },
  ];
  const remaining = steps.filter((s) => !s.done).length;
  if (remaining === 0) return null;
  const firstTodo = steps.findIndex((s) => !s.done);
  return (
    <HomeCard
      title="Set up your company"
      count={remaining}
      data-slot="setup-checklist"
      action={
        steps.length - remaining >= 4 ? (
          <Button variant="ghost" size="sm" onClick={onDismiss}>
            Dismiss
          </Button>
        ) : null
      }
    >
      <SetupSteps
        aria-label="Setup steps"
        steps={steps.map((s, i) => ({
          label: s.label,
          hint: s.done ? "Done" : s.hint,
          state: s.done ? "done" : i === firstTodo ? "current" : "todo",
          onSelect: () => (s.quick && quickAdd ? quickAdd(s.quick) : go(s.to)),
        }))}
      />
    </HomeCard>
  );
}

// Recent and Focus --------------------------------------------------------------------------------

function RecentCard({ companyId }: { companyId: string }) {
  const [places] = useState<RecentPlace[]>(() => readRecent(companyId));
  return (
    <HomeCard title="Recent" flush data-slot="home-recent">
      {/* Nothing opened yet: one muted line. */}
      {places.length === 0 ? (
        <p className="flex min-h-9 items-center gap-2 px-3 text-xs text-muted-foreground">
          <HistoryIcon aria-hidden="true" className="size-3.5 text-faint" />
          Places you open show up here.
        </p>
      ) : null}
      {places.map((p) => (
        <HomeCardRow
          key={p.href}
          className="min-h-9 py-1"
          lead={<HistoryIcon aria-hidden="true" className="size-4 text-faint" />}
          title={
            <Link to={p.href as never} className="truncate hover:underline">
              {p.label}
            </Link>
          }
          trailing={<span className="text-xs text-muted-foreground">{p.mode}</span>}
        />
      ))}
    </HomeCard>
  );
}

function FocusCard() {
  const { focus, setFocus } = useFocus();
  const name = useSubjectName(focus.subject);
  const empty = !focus.subject && !focus.period;
  // Nothing in focus: no card (the chips row is where a focus is picked).
  if (empty) return null;
  return (
    <HomeCard
      title="Focus"
      data-slot="home-focus"
      action={
        empty ? null : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFocus({ subject: null, period: null })}
          >
            Clear
          </Button>
        )
      }
    >
      {empty ? (
        <p className="text-sm text-muted-foreground">
          Nothing in focus — pick a seat or a week and it follows you across modes.
        </p>
      ) : (
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
          {focus.subject ? (
            <>
              <dt className="text-muted-foreground">Subject</dt>
              <dd className="min-w-0 truncate" title={formatSubject(focus.subject)}>
                {name}
              </dd>
            </>
          ) : null}
          {focus.period ? (
            <>
              <dt className="text-muted-foreground">Week</dt>
              <dd>
                W{focus.period.slice(-2)} · {weekRangeLabel(focus.period)}
              </dd>
            </>
          ) : null}
        </dl>
      )}
    </HomeCard>
  );
}
